'use client';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Image from 'next/image';

/** Outlined fields on the signed-out pages: quiet border, black focus ring instead of the theme's default. */
export const authInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'background.paper',
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'text.disabled' },
    '&.Mui-focused fieldset': { borderColor: 'text.primary', borderWidth: '1px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: 'text.primary' },
} as const;

/** The full-width submit button of a signed-out form, with a spinner while it works. */
export function AuthSubmitButton({ loading, disabled, children }: { loading: boolean; disabled?: boolean; children: React.ReactNode }) {
  return (
    <Button
      type="submit"
      variant="contained"
      fullWidth
      disableElevation
      disabled={loading || disabled}
      sx={{
        mt: 1,
        height: 44,
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'none',
        // While it works the button keeps its colour; only a form that isn't ready greys it out.
        ...(loading && { '&.Mui-disabled': { bgcolor: 'primary.main', color: 'primary.contrastText', opacity: 0.8 } }),
      }}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : children}
    </Button>
  );
}

/** Signed-out pages (sign in, accept invitation): the black brand panel, and the page's form in a narrow column beside it. */
export default function LoginLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box component="main" sx={{ minHeight: '100vh', width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Brand panel — mirrors the app's black AppBar */}
      <Box
        sx={{
          backgroundColor: '#000000',
          color: '#ffffff',
          flex: { xs: '0 0 auto', md: '0 0 44%' },
          minHeight: { xs: 180, md: '100vh' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'center', md: 'space-between' },
          alignItems: { xs: 'center', md: 'flex-start' },
          px: { xs: 3, md: 6 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Image src="/logo.png" alt="TLC" width={40} height={40} priority style={{ objectFit: 'contain' }} />
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
            TLC CRM
          </Typography>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 420 }}>
          <Typography variant="h3" sx={{ color: '#ffffff', fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.02em', mb: 2 }}>
            The Timeless Collector
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
            Inventory, orders, and operations
          </Typography>
        </Box>

        {/* Keeps the headline centred between the logo and the bottom edge */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }} />
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          px: { xs: 3, sm: 5 },
          py: { xs: 5, md: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          {children}
          <Typography variant="caption" sx={{ display: 'block', mt: 4, color: 'text.secondary', fontSize: '0.7rem', letterSpacing: '0.04em' }}>
            © {new Date().getFullYear()} The Timeless Collector
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
